import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress,
  Alert, Stepper, Step, StepLabel, StepContent, TextField, Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { submitKyc, getKycStatus, submitAccreditation, getAccreditationStatus } from '../services/api';

const STEPS = ['Upload ID Document', 'Upload Selfie', 'Accreditation Documents', 'Review & Submit'];

const FileUploadBox = ({ label, icon, file, onFileChange, accept = 'image/*,.pdf' }) => {
  const ref = useRef();
  return (
    <Box
      onClick={() => ref.current?.click()}
      sx={{
        border: '2px dashed', borderColor: file ? '#4caf50' : '#ccc', borderRadius: 2,
        p: 4, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
        '&:hover': { borderColor: '#478289', backgroundColor: '#f5f5f5' },
        backgroundColor: file ? '#f1f8e9' : 'transparent',
      }}
    >
      <input ref={ref} type="file" hidden accept={accept} onChange={(e) => onFileChange(e.target.files[0])} />
      {file ? <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} /> : icon}
      <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
        {file ? file.name : label}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {file ? 'Click to change' : 'Click to upload or drag & drop'}
      </Typography>
    </Box>
  );
};

const KYCFlow = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [accreditationDoc, setAccreditationDoc] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [accredStatus, setAccredStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [kycRes, accRes] = await Promise.all([getKycStatus(), getAccreditationStatus()]);
        setKycStatus(kycRes.data);
        setAccredStatus(accRes.data);
        // If already approved, show completion
        if (kycRes.data?.status === 'approved' && accRes.data?.status === 'approved') {
          setActiveStep(4);
          setSuccess(true);
        }
      } catch (_) { /* first time */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Submit KYC
      const kycForm = new FormData();
      if (idDoc) kycForm.append('idDocument', idDoc);
      if (selfie) kycForm.append('selfie', selfie);
      await submitKyc(kycForm);

      // Submit Accreditation
      if (accreditationDoc) {
        const accForm = new FormData();
        accForm.append('document', accreditationDoc);
        await submitAccreditation(accForm);
      }

      setSuccess(true);
      setActiveStep(4);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  // Already submitted / approved state
  if (success || kycStatus?.status === 'pending') {
    return (
      <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
          <Container maxWidth="lg">
            <Typography variant="h3" sx={{ fontWeight: 700 }}>KYC Verification</Typography>
          </Container>
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              {kycStatus?.status === 'approved' ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Verified!</Typography>
                  <Typography color="textSecondary">Your identity and accreditation have been verified.</Typography>
                </>
              ) : (
                <>
                  <VerifiedUserIcon sx={{ fontSize: 80, color: '#478289', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Under Review</Typography>
                  <Typography color="textSecondary" sx={{ mt: 1 }}>
                    Your documents have been submitted and are being reviewed. We'll notify you once verification is complete.
                  </Typography>
                  <Chip label="Pending Review" color="warning" sx={{ mt: 2 }} />
                </>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>KYC Verification</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Complete verification to start investing</Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 0: ID Document */}
              <Step>
                <StepLabel>Upload Government-Issued ID</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Upload a clear photo of your passport, driver's license, or national ID card.
                  </Typography>
                  <FileUploadBox
                    label="Upload ID Document"
                    icon={<BadgeIcon sx={{ fontSize: 48, color: '#478289' }} />}
                    file={idDoc} onFileChange={setIdDoc}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" disabled={!idDoc} onClick={() => setActiveStep(1)}
                      sx={{ backgroundColor: '#478289' }}>Continue</Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 1: Selfie */}
              <Step>
                <StepLabel>Upload Selfie</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Take a clear selfie holding your ID next to your face.
                  </Typography>
                  <FileUploadBox
                    label="Upload Selfie"
                    icon={<CameraAltIcon sx={{ fontSize: 48, color: '#478289' }} />}
                    file={selfie} onFileChange={setSelfie} accept="image/*"
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button onClick={() => setActiveStep(0)}>Back</Button>
                    <Button variant="contained" disabled={!selfie} onClick={() => setActiveStep(2)}
                      sx={{ backgroundColor: '#478289' }}>Continue</Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Accreditation */}
              <Step>
                <StepLabel>Accreditation Documents</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Upload proof of accredited investor status (tax returns, bank statement, CPA letter, etc.)
                  </Typography>
                  <FileUploadBox
                    label="Upload Accreditation Proof"
                    icon={<VerifiedUserIcon sx={{ fontSize: 48, color: '#478289' }} />}
                    file={accreditationDoc} onFileChange={setAccreditationDoc}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button onClick={() => setActiveStep(1)}>Back</Button>
                    <Button variant="contained" onClick={() => setActiveStep(3)}
                      sx={{ backgroundColor: '#478289' }}>Continue</Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Review */}
              <Step>
                <StepLabel>Review & Submit</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>Please review your uploads before submitting:</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip icon={idDoc ? <CheckCircleIcon /> : undefined}
                      label={`ID: ${idDoc?.name || 'Not uploaded'}`}
                      color={idDoc ? 'success' : 'default'} sx={{ mr: 1, mb: 1 }} />
                    <Chip icon={selfie ? <CheckCircleIcon /> : undefined}
                      label={`Selfie: ${selfie?.name || 'Not uploaded'}`}
                      color={selfie ? 'success' : 'default'} sx={{ mr: 1, mb: 1 }} />
                    <Chip icon={accreditationDoc ? <CheckCircleIcon /> : undefined}
                      label={`Accreditation: ${accreditationDoc?.name || 'Optional'}`}
                      color={accreditationDoc ? 'success' : 'default'} sx={{ mb: 1 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={() => setActiveStep(2)}>Back</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={submitting || !idDoc}
                      sx={{ backgroundColor: '#478289' }}>
                      {submitting ? <CircularProgress size={20} /> : 'Submit for Verification'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default KYCFlow;
